import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, X } from 'lucide-react';

interface GuidedTourProps {
  active: boolean;
  targetId: string;         // element to spotlight (the Setup Guide checklist)
  onDismiss: () => void;
  done?: number;            // completed steps (can complete out of order)
  totalSteps?: number;
  nextLabel?: string | null;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Lightweight spotlight overlay. It dims the whole screen except the target
// element (the Setup Guide) using a giant box-shadow cutout, and shows a coach
// tooltip beside it. The overlay is pointer-events:none so the player still
// clicks the real checklist through the cutout — only the tooltip is interactive.
// When the target isn't on the page (e.g. setup complete → checklist unmounts),
// nothing renders, so the tour ends itself.
const GuidedTour: React.FC<GuidedTourProps> = ({ active, targetId, onDismiss, done, totalSteps, nextLabel }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);

  const measure = useCallback(() => {
    const el = document.getElementById(targetId);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [targetId]);

  // When the tour becomes active, bring the target into view so the spotlight
  // isn't off-screen below the fold.
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'center',
      });
      measure();
    }, 80);
    return () => clearTimeout(t);
  }, [active, targetId, measure]);

  useEffect(() => {
    if (!active) return;
    measure();
    // Re-measure on layout changes and periodically (checklist grows/shrinks as
    // steps complete, panels open, etc.).
    const id = window.setInterval(measure, 300);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [active, measure]);

  if (!active || !rect) return null;

  const pad = 8;
  const top = rect.top - pad;
  const left = rect.left - pad;
  const width = rect.width + pad * 2;
  const height = rect.height + pad * 2;

  // Place the tooltip below the spotlight, or above if it would go off-screen.
  const tooltipBelow = top + height + 160 < window.innerHeight;
  const tooltipTop = tooltipBelow ? top + height + 12 : Math.max(12, top - 150);
  const pulse = !prefersReducedMotion();

  // Sit below modals/drawer (z-50 / z-40) so any panel that opens covers the
  // tour instead of the tour dimming the panel.
  return (
    <div className="fixed inset-0 z-30 pointer-events-none" role="dialog" aria-label="Setup guide tour">
      {/* Spotlight cutout — transparent center, dimmed everywhere else */}
      <div
        className="absolute rounded-xl transition-all duration-300"
        style={{
          top,
          left,
          width,
          height,
          boxShadow: '0 0 0 9999px rgba(2, 6, 23, 0.72)',
        }}
      />
      {/* Pulsing highlight ring (separate element so only the ring animates) */}
      <div
        className={`absolute rounded-xl transition-all duration-300 ${pulse ? 'animate-pulse' : ''}`}
        style={{
          top,
          left,
          width,
          height,
          outline: '2px solid rgba(96, 165, 250, 0.95)',
          outlineOffset: '2px',
          boxShadow: '0 0 22px 4px rgba(59, 130, 246, 0.45)',
        }}
      />

      {/* Coach tooltip */}
      <div
        className="absolute pointer-events-auto w-[320px] max-w-[calc(100vw-24px)] bg-gray-900 border border-blue-500/60 rounded-xl shadow-2xl p-4"
        style={{
          top: tooltipTop,
          left: Math.min(Math.max(12, left), window.innerWidth - 332),
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 text-blue-300 font-semibold text-sm">
            <Sparkles className="w-4 h-4" /> Getting started
          </div>
          <button
            onClick={onDismiss}
            className="text-gray-500 hover:text-gray-300"
            title="Skip the tutorial"
            aria-label="Skip the tutorial"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {typeof done === 'number' && typeof totalSteps === 'number' && totalSteps > 0 && (
          <div className="mt-2">
            <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1">
              <span>{done} of {totalSteps} steps done</span>
              <span>{Math.round((done / totalSteps) * 100)}%</span>
            </div>
            <div className="h-1 bg-gray-800 rounded overflow-hidden">
              <div className="h-1 bg-blue-500 transition-all duration-500" style={{ width: `${(done / totalSteps) * 100}%` }} />
            </div>
          </div>
        )}

        <p className="mt-2 text-sm text-gray-300 leading-relaxed">
          Follow the <span className="text-white font-medium">Setup Guide</span> highlighted here. Each step
          unlocks the next — work down the list and click the blue button on the current step. Your first
          server starts earning once you reach the internet.
        </p>
        {nextLabel && (
          <p className="mt-2 text-xs text-amber-300/90">Do this next: <span className="font-medium">{nextLabel}</span></p>
        )}
        <div className="mt-3 flex justify-end">
          <button
            onClick={onDismiss}
            className="text-xs px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300"
          >
            Got it — hide this
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
